import { Stack, Typography, useTheme } from "@mui/material";
import { Users } from "lucide-react";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type {
	LeasingDetailDataDTO,
	LoanDetailDataDTO,
	LoanForBusinessDetailDataDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DataLineChart } from "web_myplann/Portfolio/Components/DataLineChart/DataLineChart";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { SectionDivider } from "web_myplann/Portfolio/Components/SectionDidiver";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import { maturityFormatter } from "web_myplann/Portfolio/Helpers/maturityFormatter";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail;

export const LoanAll: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailSectionLoader />;
	}

	const { daysData, totalValue, toDate, showChartInfo } = calculateGraphData(
		reporting?.reportingData,
		reporting?.summary ?? undefined,
	);

	const portfolioData = baseData.data as LoanForBusinessDetailDataDTO &
		LoanDetailDataDTO &
		LeasingDetailDataDTO;

	const interestRate = portfolioData.interestRate
		? `${portfolioData.interestRate} % p.a.`
		: EMPTY_DETAIL_FIELD;

	const installment = portfolioData.installment
		? getPortfolioFrequencyValue(
				portfolioData.installment ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const bottomComponent =
		baseData.signedAt ||
		baseData.startAt ||
		baseData.endAt ||
		portfolioData.fixationEndDate ? (
			<Stack>
				<DataRow
					label={t(PageResources.SharedLabels.signDate)}
					value={dateFormatter(baseData.signedAt)}
				/>
				<DataRow
					label={t(PageResources.SharedLabels.beginDate)}
					value={dateFormatter(baseData.startAt)}
				/>
				<DataRow
					label={t(PageResources.SharedLabels.endDate)}
					value={dateFormatter(baseData.endAt)}
				/>
				<DataRow
					label={t(PageResources.LoanForBusiness.endOfFixation)}
					value={dateFormatter(portfolioData.fixationEndDate)}
				/>
			</Stack>
		) : null;

	const basicInfoItems: DataRowCardItem[] = [
		{ label: t(PageResources.SharedLabels.name), value: baseData.clientName },
		{
			label: t(PageResources.SharedLabels.contractNumber),
			value: baseData.contractNumber,
		},
		{
			label: t(PageResources.SharedLabels.producent),
			value: baseData.producer,
		},
		{ label: t(PageResources.SharedLabels.product), value: baseData.product },
	];

	return (
		<>
			<DataLineChart
				loanChart
				hideDivider
				totalValue={totalValue}
				totalEvaluationValue={reporting?.summary?.totalEvaluationValue ?? 0}
				totalEvaluationPercent={reporting?.summary?.totalEvaluationPercent ?? 0}
				daysData={daysData}
				currencySign={currencySign}
				toDate={toDate}
				showChartInfo={showChartInfo}
				bottomComponent={
					<Stack>
						<DataRow
							label={t(PageResources.Loan.amount)}
							value={moneyFormatter(
								portfolioData.originalObligationAmount,
								currencySign,
							)}
						/>
						<DataRow
							label={t(PageResources.Loan.matury)}
							value={maturityFormatter(baseData.maturityLengthMonths ?? 0, t)}
						/>
						<DataRow
							label={t(PageResources.Loan.interestRate)}
							value={interestRate}
						/>
						<DataRow
							label={t(PageResources.Loan.installment)}
							value={installment}
						/>
					</Stack>
				}
			/>

			{daysData.length !== 0 && <SectionDivider />}

			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={bottomComponent}
				noDivider={
					!portfolioData?.coDebtors || portfolioData?.coDebtors?.length === 0
				}
				items={basicInfoItems}
			/>

			{portfolioData?.coDebtors && portfolioData?.coDebtors?.length > 0 && (
				<DataCard
					icon={
						<Users
							size={theme.iconSize.sm}
							color={theme.palette.deprecated.grey[700]}
						/>
					}
					label={t(PageResources.Loan.coDebtors)}
					noDivider={
						!computeRelatedPresence(documents?.data, baseData.related).hasAny
					}
				>
					<Stack gap={1.2}>
						{portfolioData?.coDebtors?.map((debtor) => (
							<Typography
								variant="deprecatedBody2"
								color={theme.palette.deprecated.grey[700]}
								key={debtor.id}
							>
								{`${debtor.firstname} ${debtor.lastname} (${debtor.identification})`}
							</Typography>
						))}
					</Stack>
				</DataCard>
			)}

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</>
	);
};
